import { DefaultButton, Panel, PanelType, PrimaryButton, Spinner, Stack } from '@fluentui/react';
import React from 'react';
import { toast } from 'react-toastify';
import { useLazyGetProcurementDemandItemQuery, useUpdateProcurementDemandItemMutation } from '../../../store/endpoints/ProcurementEndpoints';
import { IProcurementDemandItem } from '../../../models/Procurement';
import { INormalizedProduct } from '../../../models/NormalizedProduct';
import { useLazyGetItemDetailsQuery } from '../../../store/Api';
import VendorDisplay from '../../Products/VendorDisplay';

interface IReSourcePanelProps {
    demandIds: string[];
    onClose: () => void;
    onReSourceComplete: () => void;
}

const ReSourcePanel: React.FC<IReSourcePanelProps> = (props) => {
    const [loadDemand] = useLazyGetProcurementDemandItemQuery();
    const [updateDemand] = useUpdateProcurementDemandItemMutation();
    const [demands, setDemands] = React.useState<{ [key: string]: IProcurementDemandItem }>({})
    const [demandsLoading, setDemandsLoading] = React.useState<{ [key: string]: boolean }>({});
    const [demandsLoaded, setDemandsLoaded] = React.useState<boolean>(false);
    const [saving, setSaving] = React.useState(false);
    // The vendor each demand had when loaded, so we only persist the ones the user actually re-sourced.
    const originalVendors = React.useRef<{ [key: string]: string | undefined }>({});

    React.useEffect(() => {
        const fetchDemands = async () => {
            const newDemands: { [key: string]: IProcurementDemandItem } = {};
            const newLoading: { [key: string]: boolean } = {};
            for (const demandId of props.demandIds) {
                newLoading[demandId] = true;
                const result = await loadDemand({ itemId: demandId }).unwrap();
                newDemands[demandId] = result;
                newLoading[demandId] = false;
            }
            originalVendors.current = Object.fromEntries(Object.values(newDemands).map(d => [d.id, d.vendor]));
            setDemands(newDemands);
            setDemandsLoading(newLoading);
            setDemandsLoaded(true);
        }
        fetchDemands();
    }, [props.demandIds]);

    const changedDemands = Object.values(demands).filter(d => d.vendor !== originalVendors.current[d.id]);

    const save = async () => {
        if (changedDemands.length === 0) {
            props.onReSourceComplete();
            return;
        }
        setSaving(true);
        const label = `${changedDemands.length} demand item${changedDemands.length === 1 ? '' : 's'}`;
        const toastId = toast.loading(`Re-sourcing ${label}…`);
        try {
            const results = await Promise.all(changedDemands.map(d =>
                updateDemand({ itemId: d.id, update: { vendor: d.vendor } }).unwrap()
            ));
            const failed = results.filter(r => !r.success).length;
            if (failed > 0) {
                toast.update(toastId, { type: 'error', render: `Failed to re-source ${failed} of ${label}`, isLoading: false, autoClose: 4000 });
            } else {
                toast.update(toastId, { type: 'success', render: `Re-sourced ${label}`, isLoading: false, autoClose: 3000 });
                props.onReSourceComplete();
            }
        } catch {
            toast.update(toastId, { type: 'error', render: `Failed to re-source ${label}`, isLoading: false, autoClose: 4000 });
        } finally {
            setSaving(false);
        }
    }

    return (
        <Panel
            type={PanelType.large}
            isOpen={true}
            onDismiss={props.onClose}
            headerText={`Re-Source Demands (${props.demandIds.length})`}
            isFooterAtBottom={true}
            onRenderFooterContent={() => (
                <Stack horizontal horizontalAlign='end' tokens={{ childrenGap: '1em' }}>
                    <DefaultButton text='Cancel' onClick={props.onClose} disabled={saving} />
                    <PrimaryButton
                        text='Save'
                        iconProps={{ iconName: 'Save' }}
                        onClick={save}
                        disabled={saving || !demandsLoaded || changedDemands.length === 0}
                    />
                </Stack>
            )}
        >
            {!demandsLoaded
                ? <Spinner label='Loading demands...' />
                : <ReSourceProductsTable
                    demands={demands}
                    onChange={(updatedDemands) => setDemands(updatedDemands)}
                />
            }

        </Panel>
    );
}

interface IReSourceProductsTable {
    demands: { [key: string]: IProcurementDemandItem };
    onChange: (updatedDemands: { [key: string]: IProcurementDemandItem }) => void;
}

const ReSourceProductsTable: React.FC<IReSourceProductsTable> = (props) => {
    const [getProduct] = useLazyGetItemDetailsQuery();
    const [products, setProducts] = React.useState<{ [key: string]: INormalizedProduct}>({});
    const [vendors, setVendors] = React.useState<string[]>([]);

    React.useEffect(() => {
        const fetchProducts = async () => {
            const newProducts: { [key: string]: INormalizedProduct } = {};
            setProducts(prevProducts => {
                const demandsToFetch = Object.values(props.demands).filter(
                    demand => !prevProducts[demand.productId]
                );
                if (demandsToFetch.length === 0) return prevProducts;
                const fetchPromises = demandsToFetch.map(async demand => {
                    const result = await getProduct({ catalog: demand.catalog, id: demand.productId }).unwrap();
                    newProducts[demand.productId] = result;
                });
                Promise.all(fetchPromises).then(() => {
                    setProducts(prev => ({ ...prev, ...newProducts }));
                });
                return prevProducts;
            });
        };
        fetchProducts();
    }, [props.demands]);

    React.useEffect(() => {
        const uniqueVendors: Set<string> = new Set();
        for (const product of Object.values(products)) {
            if (!product.vendors) continue;
            for (const vendor of product.vendors) {
                uniqueVendors.add(vendor.vendorName);
            }
        }
        setVendors(Array.from(uniqueVendors));
    }, [products]);

    return (
        <div>
            <table className='data-table'>
                <thead>
                    <tr>
                        <th>Product</th>
                        {vendors.map((vendor) => (<th key={vendor} style={{width: '150px'}}>{vendor}</th>) )}
                    </tr>
                </thead>
                <tbody>
                    {Object.values(props.demands).map((demand, index) => (
                        <tr key={index}>
                            <td>{demand.productName}</td>
                            {vendors.map((vendor) => {
                                const product = products[demand.productId];
                                const vendorInfo = product?.vendors?.find(v => v.vendorName === vendor && ((v.onHand ?? 0) > 0 || v.inStock))
                                    ?? product?.vendors?.find(v => v.vendorName === vendor);
                                const inStock = vendorInfo ? (vendorInfo.onHand ?? 0) > 0 || vendorInfo.inStock : false;
                                return (
                                    <td
                                        onClick={() => {
                                            const updatedDemands = { ...props.demands };
                                            const updatedDemand = { ...updatedDemands[demand.id] };
                                            updatedDemand.vendor = vendor;
                                            updatedDemands[demand.id] = updatedDemand;
                                            props.onChange(updatedDemands);
                                        }}
                                        key={vendor}
                                        style={{
                                            backgroundColor: inStock ? '#e2e9cb' : '#ddd',
                                            border: demand.vendor === vendor ? '5px solid rgb(120, 156, 47)' : undefined,
                                        }}
                                    >
                                        {vendorInfo ? (
                                            <VendorDisplay vendor={[vendorInfo]} product={product} />
                                        ) : null}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export default ReSourcePanel;